import type { QuestionTypes } from '@/types';
import { useTranslation } from 'react-i18next';
import { useFormResponseStore } from '@/store';
import React, { useEffect, useState } from 'react';

interface CheckboxProps {
  question: QuestionTypes;
  sectionId: string;
  sectionName: string;
  setValidationErrors: (sectionId: string, questionId: string, error: string | null) => void;
}

export const CheckboxAnswer: React.FC<CheckboxProps> = ({ 
  question, 
  sectionId, 
  sectionName, 
  setValidationErrors 
}) => {
  const { t } = useTranslation();
  const { formResponses, setResponse } = useFormResponseStore();
  
  // Get options and urls, default to empty arrays
  const options = question.options ?? [];
  const urls = question.urls ?? [];
  
  // Get existing response from store
  const existingSection = formResponses[sectionId];
  const existingResponse = existingSection?.questions[question.id]?.response || [];
  
  // Ensure existingResponse is always an array
  const responseArray = Array.isArray(existingResponse) ? existingResponse : [];
  
  // Initialize selected options based on existing response
  const initialSelectedOptions = options.map((option: string) => 
    responseArray.some((item: any) => item.option === option && item.checked)
  );

  const [selectedOptions, setSelectedOptions] = useState<boolean[]>(initialSelectedOptions);

  // Initial validation on mount - runs ONCE like other components
  useEffect(() => {
    // Only validate if required
    if (question.required === 'yes') {
      const hasSelection = selectedOptions.some(checked => checked);
      if (!hasSelection) {
        setValidationErrors(sectionId, question.id, `${question.label} ${t('forms.isRequired')}`);
      }
    }
  }, []); // Empty deps - runs only on mount

  // Validate checkbox selection based on all rules
  const validateSelection = (updatedOptions: boolean[]): string | null => {
    const numSelected = updatedOptions.filter(checked => checked).length;
    const totalOptions = options.length;
    
    // Check if required and nothing selected
    if (question.required === 'yes' && numSelected === 0) {
      return `${question.label} ${t('forms.isRequired')}`;
    }

    // Check numberOfOptions validation rules
    if (question.numberOfOptions && question.selectOptions) {
      const requiredCount = Number(question.numberOfOptions);
      
      // Fix: If requiredCount exceeds available options, treat as "select all"
      if (question.selectOptions === 'select-at-least') {
        const effectiveMin = Math.min(requiredCount, totalOptions);
        if (numSelected < effectiveMin) {
          return t('forms.selectAtLeast', { count: effectiveMin });
        }
      }
      
      if (question.selectOptions === 'select-at-most' && numSelected > requiredCount) {
        return t('forms.selectAtMost', { count: requiredCount });
      }
      
      if (question.selectOptions === 'select-exactly') {
        // Fix: If requiredCount exceeds available options, require all
        const effectiveCount = Math.min(requiredCount, totalOptions);
        if (numSelected !== effectiveCount) {
          return t('forms.selectExactly', { count: effectiveCount });
        }
      }
    }

    return null; // No validation error
  };

  const handleCheckboxChange = (index: number) => {
    // Create updated options array
    const updatedOptions = selectedOptions.map((checked, i) =>
      i === index ? !checked : checked
    );
    
    // Update local state
    setSelectedOptions(updatedOptions);

    // Create response details with all information
    const responseDetails = options.map((option: string, i: number) => ({
      option,
      checked: updatedOptions[i],
      url: urls[i] || '',
    }));

    // Save response to store
    setResponse(
      sectionId,
      sectionName,
      question.id,
      question.type,
      question.label as string,
      responseDetails
    );

    // Validate and update validation errors
    const validationError = validateSelection(updatedOptions);
    setValidationErrors(sectionId, question.id, validationError);
  };

  return (
    <div className="mb-4">
      <label className="main-label">
        {question.label} {question.required === 'yes' && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-2">
        {options.map((option: string, index: number) => (
          <div key={index} className="flex items-center my-1">
            <input
              type="checkbox"
              id={`checkbox-${question.id}-${index}`}
              className="h-4 w-4 mr-2 border-none accent-[#001A55]"
              checked={selectedOptions[index] || false}
              onChange={() => handleCheckboxChange(index)}
            />
            <label htmlFor={`checkbox-${question.id}-${index}`} className="main-label">
              {urls[index] ? (
                <a
                  href={urls[index] as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-darkBlue hover:underline"
                >
                  {option}
                </a>
              ) : (
                option
              )}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};
